import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
    ArrowLeft, 
    ArrowRight, 
    BookOpen, 
    FileText,
    ChevronRight,
    AlertCircle,
    Heart,
    CheckCircle2,
    Lightbulb,
    ShieldAlert
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import Layout from '../../components/Layout';
import { getChapter } from '../../lib/api';
import { toast } from 'sonner';
import VideoPlayer from '../../components/VideoPlayer';

const PSCChapterDetail = () => {
    const { chapterId } = useParams();
    const [chapter, setChapter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeFiche, setActiveFiche] = useState(0);

    useEffect(() => {
        loadData();
    }, [chapterId]);

    const loadData = async () => {
        try {
            const chapterData = await getChapter(chapterId);
            setChapter(chapterData);
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        // Si le contenu contient des balises HTML (éditeur riche)
        if (htmlContent.includes('<p>') || htmlContent.includes('<h1>') || htmlContent.includes('<div>')) {
            return (
                <div 
                    className="prose prose-slate max-w-none rich-text-content"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            );
        }
        
        const clean = htmlContent.split('\n').map(line => line.trim()).filter(line => line && !/^[━─—_-]{5,}$/.test(line));
        const sections = [];
        let current = { title: '', lines: [] };
        const isHeading = (line) => {
            const value = line.replace(/^#{1,4}\s*/, '').replace(/\*\*/g, '').trim();
            return value.length > 2 && value.length < 70 && value === value.toLocaleUpperCase('fr-FR') && /[A-ZÀ-ÖØ-Ý]/.test(value);
        };
        clean.forEach((line) => {
            if (isHeading(line)) {
                if (current.title || current.lines.length) sections.push(current);
                current = { title: line.replace(/^#{1,4}\s*/, '').replace(/\*\*/g, ''), lines: [] };
            } else current.lines.push(line);
        });
        if (current.title || current.lines.length) sections.push(current);

        const styleFor = (title) => {
            const t = title.toUpperCase();
            if (/DANGER|URGENCE|ATTENTION|ERREUR/.test(t)) return { box: 'border-red-200 bg-red-50', icon: <ShieldAlert className="w-5 h-5 text-red-600" />, title: 'text-red-900' };
            if (/CONDUITE|ACTION|GESTE|QUE FAIRE|TECHNIQUE/.test(t)) return { box: 'border-blue-200 bg-blue-50', icon: <CheckCircle2 className="w-5 h-5 text-blue-600" />, title: 'text-blue-900' };
            if (/SIGNE|RECONNAÎTRE|SITUATION|CAUSE/.test(t)) return { box: 'border-amber-200 bg-amber-50', icon: <Lightbulb className="w-5 h-5 text-amber-600" />, title: 'text-amber-900' };
            return { box: 'border-slate-200 bg-white', icon: <BookOpen className="w-5 h-5 text-green-600" />, title: 'text-slate-900' };
        };
        const lineView = (line, index) => {
            const numbered = /^\d+[.)]\s*/.test(line);
            const bullet = /^(→|•|-|✓|✔)\s*/.test(line);
            const text = line.replace(/^(→|•|-|✓|✔)\s*/, '').replace(/^\d+[.)]\s*/, '');
            if (numbered || bullet) return <div key={index} className="flex items-start gap-3 py-1.5"><span className={`mt-1 flex h-5 min-w-5 items-center justify-center rounded-full text-xs font-bold ${numbered ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700'}`}>{numbered ? line.match(/^\d+/)?.[0] : '✓'}</span><p className="text-[16px] leading-7 text-slate-700">{text}</p></div>;
            return <p key={index} className="text-[16px] leading-7 text-slate-700 py-1">{text}</p>;
        };
        return <div className="space-y-5">{sections.map((section, index) => {
            const style = styleFor(section.title);
            return <section key={index} className={`rounded-2xl border p-5 sm:p-6 shadow-sm ${style.box}`}>
                {section.title && <div className="flex items-center gap-3 mb-3">{style.icon}<h2 className={`text-lg sm:text-xl font-extrabold tracking-tight ${style.title}`}>{section.title}</h2></div>}
                <div>{section.lines.map(lineView)}</div>
            </section>;
        })}</div>;
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="spinner"></div>
                </div>
            </Layout>
        );
    }

    if (!chapter) {
        return (
            <Layout>
                <div className="max-w-7xl mx-auto px-4 py-8 text-center">
                    <p className="text-slate-600">Chapitre non trouvé</p>
                    <Link to="/psc" className="mt-4 inline-block">
                        <Button variant="outline">Retour aux chapitres PSC</Button>
                    </Link>
                </div>
            </Layout>
        );
    }

    const currentFiche = chapter.fiches?.[activeFiche];

    return (
        <Layout>
            <div className="min-h-screen bg-slate-50/80 pb-16" data-testid="psc-chapter-detail">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                {/* Disclaimer Banner */}
                <div className="mb-5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-amber-800 mb-1">Plateforme de révision uniquement</p>
                            <p className="text-sm text-amber-700">
                                Ce contenu ne remplace pas une formation officielle PSC1 en centre agréé.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-6">
                    <Link to="/" className="hover:text-green-600 transition-colors">
                        Accueil
                    </Link>
                    <ChevronRight className="w-4 h-4" />
                    <Link to="/psc" className="hover:text-green-600 transition-colors">
                        PSC - Premiers Secours Citoyen
                    </Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-slate-900 font-medium">{chapter.titre}</span>
                </div>

                {/* Header */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 via-green-700 to-teal-800 text-white p-6 sm:p-8 mb-7 shadow-xl shadow-emerald-900/10">
                    <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-white/10" />
                    <div className="relative flex-1">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-white/15 border border-white/20 rounded-2xl flex items-center justify-center">
                                <Heart className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <span className="text-sm font-semibold text-emerald-100">Chapitre {chapter.numero} · Révision PSC</span>
                                <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                                    {chapter.titre}
                                </h1>
                            </div>
                        </div>
                        <p className="text-emerald-50/90 max-w-3xl text-base sm:text-lg leading-relaxed">
                            {chapter.description}
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-6 items-start">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <Card className="sticky top-20 rounded-2xl border-slate-200 shadow-sm overflow-hidden" data-testid="fiches-sidebar">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Fiches ({chapter.fiches?.length || 0})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ScrollArea className="max-h-[520px]">
                                    <div className="px-3 pb-4 space-y-2">
                                        {chapter.fiches?.map((fiche, index) => (
                                            <button
                                                key={fiche.id}
                                                onClick={() => setActiveFiche(index)}
                                                className={`group w-full text-left px-3 py-3 rounded-xl text-sm transition-all flex items-start gap-3 ${
                                                    activeFiche === index
                                                        ? 'bg-emerald-600 text-white font-semibold shadow-md shadow-emerald-600/20'
                                                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                                }`}
                                                data-testid={`fiche-btn-${index}`}
                                            >
                                                <span className={`flex h-6 min-w-6 items-center justify-center rounded-lg text-xs font-bold ${activeFiche === index ? 'bg-white/20' : 'bg-slate-100 text-slate-500 group-hover:bg-white'}`}>{index + 1}</span>
                                                <span className="leading-5">{fiche.titre}</span>
                                            </button>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-3">
                        {/* Vidéos en haut du chapitre */}
                        <VideoPlayer 
                            chapterId={chapterId} 
                            position="top" 
                        />
                        
                        <Card className="rounded-3xl border-slate-200 shadow-lg shadow-slate-900/5 overflow-hidden" data-testid="fiche-content">
                            {currentFiche?.image_url && (
                                <div className="w-full h-64 overflow-hidden rounded-t-lg">
                                    <img 
                                        src={currentFiche.image_url} 
                                        alt={currentFiche.titre}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            <CardHeader className="border-b border-slate-100 bg-white px-5 sm:px-8 py-6">
                                <div className="mb-3 flex items-center justify-between text-xs font-semibold text-slate-500"><span>FICHE {activeFiche + 1}</span><span>{Math.round(((activeFiche + 1) / (chapter.fiches?.length || 1)) * 100)} % du chapitre</span></div>
                                <div className="h-2 rounded-full bg-slate-100 mb-5 overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{width: `${((activeFiche + 1) / (chapter.fiches?.length || 1)) * 100}%`}} /></div>
                                <CardTitle className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                                    {currentFiche?.titre}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-5 sm:p-8 bg-slate-50/40">
                                {/* Vidéos de la fiche */}
                                <VideoPlayer 
                                    chapterId={chapterId}
                                    ficheId={currentFiche?.id}
                                    position="top" 
                                />
                                
                                <div className="max-w-3xl mx-auto">
                                    {renderContent(currentFiche?.contenu || '')}
                                </div>
                                
                                {/* Vidéos en bas de la fiche */}
                                <VideoPlayer 
                                    chapterId={chapterId}
                                    ficheId={currentFiche?.id}
                                    position="bottom" 
                                />
                            </CardContent>
                        </Card>

                        {/* Navigation */}
                        <div className="flex items-center justify-between mt-6 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                            <Button
                                variant="outline"
                                onClick={() => setActiveFiche(Math.max(0, activeFiche - 1))}
                                disabled={activeFiche === 0}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Précédent
                            </Button>
                            
                            <span className="text-sm text-slate-500">
                                Fiche {activeFiche + 1} sur {chapter.fiches?.length || 0}
                            </span>
                            
                            {activeFiche < (chapter.fiches?.length || 0) - 1 ? (
                                <Button
                                    variant="outline"
                                    onClick={() => setActiveFiche(activeFiche + 1)}
                                >
                                    Suivant
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            ) : (
                                <Link to="/psc">
                                    <Button variant="outline">
                                        Retour aux chapitres
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
              </div>
            </div>
        </Layout>
    );
};

export default PSCChapterDetail;
